import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Clock, ArrowLeft, ArrowRight, BookOpen, Tag } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/sections/Navigation";
import { Footer } from "@/components/sections/Footer";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Spinner = ({ bg = "bg-[#F3F0E7]" }) => (
  <div className={`min-h-screen ${bg} pt-28 flex items-center justify-center`}>
    <div className="animate-spin w-8 h-8 border-4 border-[#003A4B] border-t-transparent rounded-full"></div>
  </div>
);

const BlogListHeader = () => (
  <div className="mb-12">
    <Link to="/" className="inline-flex items-center gap-2 text-[#003A4B] hover:text-[#C89D4B] transition-colors mb-6">
      <ArrowLeft className="w-4 h-4" /> Back to Home
    </Link>
    <div className="flex items-center gap-3 mb-2">
      <BookOpen className="w-6 h-6 text-[#C89D4B]" />
      <span className="text-[#C89D4B] font-semibold uppercase tracking-widest text-sm">Care Tips Blog</span>
    </div>
    <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-[#1A2332]">Expert Care Advice</h1>
    <p className="text-[#718096] mt-3 text-lg">Practical tips and guides for home patient care.</p>
  </div>
);

const BlogCardPreview = ({ post }) => (
  <Link key={post.id} to={`/blog/${post.id}`}>
    <Card data-testid={`blog-list-${post.id}`} className="border-0 shadow-lg hover:shadow-xl transition-all overflow-hidden bg-white group cursor-pointer">
      <div className="grid md:grid-cols-3 gap-0">
        <div className="h-56 md:h-auto overflow-hidden">
          <img src={post.image_url} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        </div>
        <CardContent className="md:col-span-2 p-8 flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-xs font-semibold text-[#003A4B] bg-[#003A4B]/10 px-3 py-1 rounded-full flex items-center gap-1"><Tag className="w-3 h-3" />{post.category}</span>
            <span className="flex items-center gap-1 text-xs text-[#718096]"><Clock className="w-3 h-3" />{post.read_time}</span>
          </div>
          <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A2332] mb-3 group-hover:text-[#003A4B] transition-colors">{post.title}</h2>
          <p className="text-[#4A5568] mb-4 leading-relaxed">{post.excerpt}</p>
          <span className="flex items-center gap-1 text-[#C89D4B] font-medium group-hover:gap-2 transition-all">
            Read Article <ArrowRight className="w-4 h-4" />
          </span>
        </CardContent>
      </div>
    </Card>
  </Link>
);

const EmptyBlogState = () => (
  <div className="text-center py-20">
    <BookOpen className="w-16 h-16 text-[#003A4B]/20 mx-auto mb-4" />
    <p className="text-[#718096] text-lg">No articles published yet.</p>
  </div>
);

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = useCallback(() => {
    axios.get(`${API}/blog`)
      .then(r => setPosts(r.data))
      .catch(() => { /* Blog list fetch failed silently */ })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  if (loading) return <Spinner />;

  return (
    <div className="min-h-screen bg-[#F3F0E7]">
      <Navigation />
      <div className="pt-28 pb-20 max-w-5xl mx-auto px-6 lg:px-12">
        <BlogListHeader />
        <div className="space-y-8">
          {posts.map((post) => <BlogCardPreview key={post.id} post={post} />)}
        </div>
        {posts.length === 0 && <EmptyBlogState />}
      </div>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

const renderParagraph = (paragraph, index) => {
  if (paragraph.startsWith('**') || paragraph.includes('**')) {
    const parts = paragraph.split(/\*\*(.*?)\*\*/g);
    return (
      <p key={`p-${index}`} className="mb-4">
        {parts.map((part, j) => j % 2 === 1 ? <strong key={`s-${index}-${j}`} className="text-[#1A2332]">{part}</strong> : part)}
      </p>
    );
  }
  return <p key={`p-${index}`} className="mb-4">{paragraph}</p>;
};

const ArticleContent = ({ content }) => (
  <div data-testid="blog-post-content" className="prose prose-lg max-w-none text-[#4A5568] leading-relaxed">
    {content.split('\n\n').map((paragraph, i) => renderParagraph(paragraph, i))}
  </div>
);

const AuthorFooter = () => (
  <div className="mt-12 pt-8 border-t border-[#003A4B]/10">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-gradient-to-br from-[#003A4B] to-[#1B5E6B] rounded-full flex items-center justify-center text-white font-bold">Y</div>
        <div>
          <p className="font-semibold text-[#1A2332]">Yahaira Caceres Zamora</p>
          <p className="text-sm text-[#718096]">Certified Caregiver - 10+ Years Experience</p>
        </div>
      </div>
      <Link to="/blog">
        <Button variant="outline" className="rounded-full border-[#003A4B]/20 text-[#003A4B]">
          More Articles <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </Link>
    </div>
  </div>
);

const NotFoundView = () => (
  <div className="min-h-screen bg-white">
    <Navigation />
    <div className="pt-28 pb-20 max-w-3xl mx-auto px-6 text-center">
      <h1 className="text-2xl font-bold text-[#1A2332] mb-4">Article not found</h1>
      <Link to="/blog"><Button className="bg-[#003A4B] text-white rounded-full">Back to Blog</Button></Link>
    </div>
    <Footer />
  </div>
);

const BlogPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPost = useCallback(() => {
    axios.get(`${API}/blog/${id}`)
      .then(r => setPost(r.data))
      .catch(() => { /* Post fetch failed - will show not found */ })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { fetchPost(); }, [fetchPost]);

  if (loading) return <Spinner bg="bg-white" />;
  if (!post) return <NotFoundView />;

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <article className="pt-28 pb-20">
        <div className="w-full h-[400px] overflow-hidden relative">
          <img src={post.image_url?.replace('w=600', 'w=1200')} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A2332]/60 to-transparent"></div>
        </div>
        <div className="max-w-3xl mx-auto px-6 lg:px-12 -mt-20 relative z-10">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            <Link to="/blog" className="inline-flex items-center gap-2 text-[#003A4B] hover:text-[#C89D4B] transition-colors mb-6 text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-semibold text-[#003A4B] bg-[#003A4B]/10 px-3 py-1 rounded-full">{post.category}</span>
              <span className="flex items-center gap-1 text-xs text-[#718096]"><Clock className="w-3 h-3" />{post.read_time}</span>
            </div>
            <h1 data-testid="blog-post-title" className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A2332] mb-6 leading-tight">{post.title}</h1>
            <ArticleContent content={post.content} />
            <AuthorFooter />
          </div>
        </div>
      </article>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

export { BlogList, BlogPost };
